import type { OrgView, Scope } from '../types/orgs'

// Display words for the org a screen is answering in. Pure: no React, no fetch, no arithmetic beyond
// counting what the API already sent. The rule these helpers hold is the reason orgs exist at all —
// a screen says WHOSE rows these are, in the org's own name, and says nothing it cannot show.

/**
 * The kicker segment for a list response: the org's name, "All orgs · 2", or "No org".
 * Returns null when the API sent no scope, so the caller renders no segment rather than a guess.
 */
export function scopeWords(scope: Scope | null | undefined): string | null {
  if (!scope) return null
  if (scope.org === 'all') return `All orgs · ${scope.orgs}`
  if (!scope.org) return 'No org'
  return scope.name || scope.org
}

/** The same words for a selection that has not been answered yet — the switcher's own control. */
export function selectionWords(selection: string, orgs: OrgView[], mine: string | null): string {
  if (selection === 'all') return 'All orgs'
  const key = selection === 'mine' ? mine : selection
  if (!key) return 'No org yet'
  return orgs.find((o) => o.key === key)?.name ?? key
}

/**
 * Whether a route answers for the selected organisation or only ever for the signed-in person.
 *
 * The switcher sits above every screen, so on an owner-only screen it makes a claim the screen cannot
 * keep: the header read "Three Point Turn" while the card beneath it read "measured from 9,070
 * messages you sent from dagee@pppllc.com". Both true; together a lie (REQ-064). Your mailbox, your
 * voice, your drafts and your own account are yours alone — the API ignores `?org=` on all of them —
 * so the shell marks the switcher rather than letting it imply otherwise.
 */
export type RouteScope = 'personal' | 'org'

// Outreach, the dashboard and the crafting table are first-person reads of the viewer's own file, but the org
// looked at bounds them too (owner, 2026-10-12: "a site-wide data filter that is impenetrable"): under another
// org they show that org's rows or say the viewer is not in it. So they are org screens, and the switcher applies.
// What stays personal is the viewer's own account: their mailbox, their voice, their jobs, their outbox.
const PERSONAL_ROUTES = ['/mailbox', '/voice', '/jobs', '/account', '/outbox']

export function routeScope(pathname: string): RouteScope {
  const path = (pathname || '/').toLowerCase()
  return PERSONAL_ROUTES.some((p) => path === p || path.startsWith(`${p}/`)) ? 'personal' : 'org'
}

/** "5 people · 12 pursuits", the line under an org's name. Counts come from the API. */
export function orgCountWords(org: OrgView): string {
  const people = `${org.members.length} ${org.members.length === 1 ? 'person' : 'people'}`
  const pursuits = `${org.pursuits} ${org.pursuits === 1 ? 'pursuit' : 'pursuits'}`
  return `${people} · ${pursuits}`
}

/** What the org's mailboxes add up to: nothing read yet is worth saying out loud. */
export function mailboxWords(org: OrgView): string {
  const connected = org.members.filter((m) => m.hasMailbox).length
  if (connected === 0) return 'no mailbox connected'
  if (connected === org.members.length) return connected === 1 ? 'mailbox connected' : 'every mailbox connected'
  return `${connected} of ${org.members.length} mailboxes connected`
}
